import React, { memo, useCallback } from 'react';
import Div from '@/shared/Div';
import Span from '@/shared/Span';
import Images from '@/shared/Image';
import Heading from '@/shared/Heading';
import ChevronUpIcon from '@/components/Icons/ChevronUpIcon';
import Link from '../Link';
import { getOrderDetailURL } from '@/utils/urls';
import Paragraph from '../Paragraph';
import classNames from 'classnames';
import { MapType } from '@/types';
import date from '@/utils/date';
import { ORDER_STATUSES } from '@/constants/order';
import Map from '@/components/Map';
import Divider from '../Divider';
import useScreen from '@/hooks/useScreen';

type OrderListCardProps = {
  id: string;
  userOrderId: null | number;
  name: string;
  img: string;
  createdOn: string;
  status?: string;
  orderItems?: any;
};

const OrderListCard = (props: OrderListCardProps | MapType) => {
  const { id, createdOn, userOrderId, status, orderItems } = props;
  const { isDesktop } = useScreen();

  const renderOrderItem = useCallback((item: any, index: number) => {
    const { properties, statusText } = item || {};
    const { name, img } = properties || {};
    const isLastItem = index === orderItems.length - 1;

    return (
      <Div className="flex flex-col gap-s3  ">
        <Div className="flex gap-s3 md:gap-s6 py-s3 md:p-0">
          <Images
            src={img}
            width={100}
            height={100}
            alt={name}
            className="w-[56px] h-[56px] md:w-[80px] md:h-[80px] "
          />

          <Div className="flex flex-col gap-s2 w-full justify-center">
            <Paragraph
              className={classNames('text-body-xs md:text-body-xs-desktop', {
                'text-cu-selected':
                  status === ORDER_STATUSES.READY_TO_BE_SHIPPED ||
                  status === ORDER_STATUSES.SHIPPED ||
                  status === ORDER_STATUSES.OUT_FOR_DELIVERY ||
                  status === ORDER_STATUSES.COMPLETED ||
                  status === ORDER_STATUSES.DELIVERED ||
                  status === ORDER_STATUSES.BACKORDERED ||
                  status === ORDER_STATUSES.IN_PROCESS,
                'text-extended-efbrand':
                  status === ORDER_STATUSES.REFUNDED ||
                  status === ORDER_STATUSES.REFUND_INITIATED ||
                  status === ORDER_STATUSES.RETURNED,
                'text-cu-positive':
                  status === ORDER_STATUSES.CREATED || status === ORDER_STATUSES.CONFIRMED,
                'text-cu-negative':
                  status === ORDER_STATUSES.FAILED ||
                  status === ORDER_STATUSES.PICKUP_REJECTED ||
                  status === ORDER_STATUSES.ON_HOLD,
                'text-cu-warning':
                  status === ORDER_STATUSES.CANCELLED ||
                  status === ORDER_STATUSES.RETURN_INITIATED ||
                  status === ORDER_STATUSES.PICKED_UP ||
                  status === ORDER_STATUSES.CANCELLATION_INITIATED ||
                  status === ORDER_STATUSES.PICKUP_SCHEDULED ||
                  status === ORDER_STATUSES.PARTIALLY_SHIPPED,
              })}
            >
              {statusText?.toUpperCase()}
            </Paragraph>
            <Paragraph className="md:text-hd-4">{name}</Paragraph>
          </Div>
        </Div>
        {!isLastItem && <Divider />}
      </Div>
    );
  }, []);
  return (
    <Link
      key={id}
      href={getOrderDetailURL(id)}
      className=" rounded-xl border border-co-bold p-s5  flex flex-col gap-s3 "
    >
      <Heading
        level={4}
        className={classNames('text-h-4 md:text-hd-4 flex items-center justify-between w-full ')}
      >
        Order #{userOrderId}
        <Span className="transform rotate-90 ">
          <ChevronUpIcon
            fill="#171717"
            width={isDesktop ? '36px' : '24px'}
            height={isDesktop ? '36px' : '24px'}
          />
        </Span>
      </Heading>

      <Map list={orderItems} render={renderOrderItem} className="flex flex-col gap-s3" />

      <Paragraph className="text-body-sm md:text-body-base text-cti-tertiary">
        Order placed on:{' '}
        {date.format(createdOn, {
          month: 'short',
          day: '2-digit',
        })}
      </Paragraph>
    </Link>
  );
};

export default memo(OrderListCard);
